import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { showAlert, showConfirm } from "../../common/sweetalert/sweetalert";
import { serverLink, projectURL } from "../../../resources/url";
import { toast } from "react-toastify";
import DataLoader from "../../common/dataLoader/dataLoader";
import { MaleAvatar, WomanAvatar } from "../../../resources/constants";

function RegenerateStudentRegNo(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [settingsList, setSettingsList] = useState([]);
    const [mode, setMode] = useState("auto"); // 'auto' or 'manual'
    const [admissionSession, setAdmissionSession] = useState("");
    const [newRegNo, setNewRegNo] = useState("");

    const [collegesList, setCollegesList] = useState([]);
    const [programmesList, setProgrammesList] = useState([]);
    const [selectedCollegeId, setSelectedCollegeId] = useState("");
    const [selectedProgId, setSelectedProgId] = useState("");

    const staffId = props.loginData[0]?.StaffID || "System";

    useEffect(() => {
        initData();
    }, []);

    const initData = async () => {
        try {
            const result = await axios.get(`${serverLink}staff/registration/academic/settings/list`);
            if (result.data && result.data.length > 0) {
                setSettingsList(result.data);
            }
            
            const collegesRes = await axios.get(`${serverLink}staff/registration/collage/option/list`);
            if (collegesRes.data) {
                setCollegesList(collegesRes.data);
            }

            const programmesRes = await axios.get(`${serverLink}staff/registration/programme/list`);
            if (programmesRes.data) {
                setProgrammesList(programmesRes.data);
            }

            setIsLoading(false);
        } catch (err) {
            console.error("Error initializing data:", err);
            setIsLoading(false);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) {
            toast.warn("Please enter a search query");
            return;
        }

        setIsLoading(true);
        try {
            const result = await axios.get(`${serverLink}staff/registration/student/search-for-regno?query=${searchQuery}`);
            setSearchResults(result.data);
            if (result.data.length === 0) {
                toast.info("No student found matching the search criteria");
            }
            setIsLoading(false);
        } catch (err) {
            console.error("Error searching for student:", err);
            toast.error("Failed to fetch search results");
            setIsLoading(false);
        }
    };

    const selectStudent = (student) => {
        setSelectedStudent(student);
        setAdmissionSession(student.ADMISSION_SESSION || "");
        setSelectedCollegeId(student.COL_ID || "");
        setSelectedProgId(student.PROG_ID || "");
        setNewRegNo("");
        setSearchResults([]);
    };

    const handleRegenerate = () => {
        if (!selectedStudent) return;

        if (mode === "auto" && !admissionSession) {
            showAlert("EMPTY FIELD", "Please select an admission session", "error");
            return;
        }

        if (mode === "manual" && !newRegNo.trim()) {
            showAlert("EMPTY FIELD", "Please enter the new registration number", "error");
            return;
        }

        const modeText = mode === "auto" 
            ? `automatically regenerate the registration number for session ${admissionSession}`
            : `manually change the registration number to "${newRegNo.trim()}"`;

        showConfirm(
            "CONFIRM OPERATION",
            `Are you sure you want to ${modeText} for student ${selectedStudent.FIRST_NAME} ${selectedStudent.LAST_NAME}? This will update the registration number across all database tables (biodata, courses, invoices, etc.).`,
            "warning"
        ).then(async (confirmed) => {
            if (confirmed) {
                executeRegenerate();
            }
        });
    };

    const executeRegenerate = async () => {
        setIsSubmitting(true);
        const payload = {
            APPID: selectedStudent.APPID,
            Mode: mode,
            AdmissionSession: admissionSession,
            NewRegNo: newRegNo.trim(),
            NewProgID: selectedProgId,
            InsertedBy: staffId
        };

        try {
            const response = await axios.post(`${serverLink}staff/registration/student/regenerate-regno`, payload);
            if (response.data.message === "success") {
                toast.success(`Registration number updated successfully to: ${response.data.newRegNo}`);
                // Refresh selected student details
                const updatedStudentResult = await axios.get(`${serverLink}staff/registration/student/search-for-regno?query=${selectedStudent.APPID}`);
                if (updatedStudentResult.data && updatedStudentResult.data.length > 0) {
                    const updated = updatedStudentResult.data[0];
                    setSelectedStudent(updated);
                    setSelectedCollegeId(updated.COL_ID || "");
                    setSelectedProgId(updated.PROG_ID || "");
                }
                setNewRegNo("");
            } else {
                showAlert("ERROR", response.data.details || "An error occurred during regeneration.", "error");
            }
            setIsSubmitting(false);
        } catch (err) {
            console.error("Error regenerating student reg no:", err);
            const errMsg = err.response?.data?.details || err.response?.data?.error || "Failed to update registration number.";
            showAlert("ERROR", errMsg, "error");
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="container-xl">
                <div className="page-header d-print-none">
                    <div className="row align-items-center">
                        <div className="col">
                            <h2 className="page-title">
                                Regenerate & Update Student Registration Number
                            </h2>
                            <div className="text-muted mt-1">
                                Search for a student to regenerate or manually update their matriculation/registration number.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <div className="row row-cards">
                        {/* Search Card */}
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Search Student</h3>
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handleSearch}>
                                        <div className="input-group">
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Search by Registration No, Application ID, Name, Email, or Telephone..." 
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                            <button className="btn btn-primary" type="submit" disabled={isLoading}>
                                                {isLoading ? (
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-search" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                        <circle cx="10" cy="10" r="7"></circle>
                                                        <line x1="21" y1="21" x2="15" y2="15"></line>
                                                    </svg>
                                                )}
                                                Search
                                            </button>
                                        </div>
                                    </form>

                                    {/* Search Results */}
                                    {searchResults.length > 0 && (
                                        <div className="table-responsive mt-4">
                                            <table className="table table-vcenter card-table">
                                                <thead>
                                                    <tr>
                                                        <th>Application ID</th>
                                                        <th>Current Reg No</th>
                                                        <th>Name</th>
                                                        <th>Programme</th>
                                                        <th>Active Session</th>
                                                        <th className="w-1">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {searchResults.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>{item.APPID}</td>
                                                            <td>
                                                                <span className="badge bg-blue-lt">
                                                                    {item.STUDENTID || "Not Enrolled"}
                                                                </span>
                                                            </td>
                                                            <td>{`${item.FIRST_NAME} ${item.MIDDLE_NAME || ""} ${item.LAST_NAME}`}</td>
                                                            <td className="text-muted">{item.PROGRAMME}</td>
                                                            <td className="text-muted">{item.ADMISSION_SESSION}</td>
                                                            <td>
                                                                <button 
                                                                    className="btn btn-primary btn-sm"
                                                                    onClick={() => selectStudent(item)}
                                                                >
                                                                    Select
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Student Details and Actions */}
                        {selectedStudent && (
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header bg-primary-lt">
                                        <h3 className="card-title">Student Profile</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="row align-items-center mb-4">
                                            <div className="col-auto">
                                                <span 
                                                    className="avatar avatar-xl" 
                                                    style={{ 
                                                        backgroundImage: selectedStudent.PICTURE 
                                                            ? `url(${projectURL}assets/passport/${selectedStudent.PICTURE})` 
                                                            : `url(${selectedStudent.GENDER === "Male" ? MaleAvatar : WomanAvatar})`
                                                    }}
                                                />
                                            </div>
                                            <div className="col">
                                                <h3 className="h1 mb-1">
                                                    {`${selectedStudent.FIRST_NAME} ${selectedStudent.MIDDLE_NAME || ""} ${selectedStudent.LAST_NAME}`}
                                                </h3>
                                                <div className="row g-2">
                                                    <div className="col-auto">
                                                        <span className="badge bg-green-lt">
                                                            Reg No: {selectedStudent.STUDENTID || "Not Enrolled"}
                                                        </span>
                                                    </div>
                                                    <div className="col-auto">
                                                        <span className="badge bg-secondary-lt">
                                                            App ID: {selectedStudent.APPID}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            {/* Details column */}
                                            <div className="col-md-6 border-end">
                                                <div className="hr-text hr-text-left mt-0"><h3>Academic Details</h3></div>
                                                <table className="table table-borderless table-sm">
                                                    <tbody>
                                                        <tr>
                                                            <td className="font-weight-bold" style={{ width: "35%" }}>Programme:</td>
                                                            <td>{selectedStudent.PROGRAMME}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-bold">College:</td>
                                                            <td>{selectedStudent.COL_CODE}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-bold">Department ID:</td>
                                                            <td>{selectedStudent.DEPART_ID}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-bold">Student Level:</td>
                                                            <td>{selectedStudent.STUDENT_LEVEL ? `${selectedStudent.STUDENT_LEVEL}00 Level` : "N/A"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-bold">Admission Session:</td>
                                                            <td>{selectedStudent.ADMISSION_SESSION}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-bold">Mode of Entry:</td>
                                                            <td>{selectedStudent.MODE_OF_ENTRY || "N/A"}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-bold">Email:</td>
                                                            <td>{selectedStudent.EMAIL}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="font-weight-bold">Telephone:</td>
                                                            <td>{selectedStudent.TELEPHONE}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Action column */}
                                            <div className="col-md-6">
                                                <div className="hr-text hr-text-left mt-md-0"><h3>Regenerate / Update Action</h3></div>
                                                
                                                <div className="mb-3">
                                                    <label className="form-label">College (Optional change)</label>
                                                    <select
                                                        className="form-control form-select"
                                                        value={selectedCollegeId}
                                                        onChange={(e) => {
                                                            const colId = e.target.value;
                                                            setSelectedCollegeId(colId);
                                                            const filteredProgs = programmesList.filter(p => p.COL_ID.toString() === colId.toString());
                                                            if (filteredProgs.length > 0) {
                                                                setSelectedProgId(filteredProgs[0].PROG_ID);
                                                            } else {
                                                                setSelectedProgId("");
                                                            }
                                                        }}
                                                    >
                                                        <option value="">Select College</option>
                                                        {collegesList.map((col, index) => (
                                                            <option key={index} value={col.COL_ID}>
                                                                {col.COL_CODE} - {col.COLLEGE}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">Programme (Optional change)</label>
                                                    <select
                                                        className="form-control form-select"
                                                        value={selectedProgId}
                                                        onChange={(e) => setSelectedProgId(e.target.value)}
                                                    >
                                                        <option value="">Select Programme</option>
                                                        {programmesList
                                                            .filter(p => p.COL_ID.toString() === selectedCollegeId.toString())
                                                            .map((prog, index) => (
                                                                <option key={index} value={prog.PROG_ID}>
                                                                    {prog.PROGRAMME} ({prog.PROG_CODE})
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">Regeneration Mode</label>
                                                    <div className="form-selectgroup form-selectgroup-boxes d-flex flex-column gap-2">
                                                        <label className="form-selectgroup-item flex-fill">
                                                            <input 
                                                                type="radio" 
                                                                name="mode" 
                                                                value="auto" 
                                                                className="form-selectgroup-input" 
                                                                checked={mode === "auto"}
                                                                onChange={() => setMode("auto")}
                                                            />
                                                            <span className="form-selectgroup-label d-flex align-items-center p-3">
                                                                <span className="me-3">
                                                                    <span className="form-selectgroup-check"></span>
                                                                </span>
                                                                <span className="form-selectgroup-label-content text-start">
                                                                    <span className="form-selectgroup-title">Auto-Regenerate Registration No</span>
                                                                    <span className="text-muted d-block mt-1">
                                                                        Automatically increments sequence for College: <strong>{
                                                                            (collegesList.find(c => c.COL_ID.toString() === selectedCollegeId.toString())?.COL_CODE) || selectedStudent.COL_CODE
                                                                        }</strong>
                                                                    </span>
                                                                </span>
                                                            </span>
                                                        </label>
                                                        <label className="form-selectgroup-item flex-fill">
                                                            <input 
                                                                type="radio" 
                                                                name="mode" 
                                                                value="manual" 
                                                                className="form-selectgroup-input"
                                                                checked={mode === "manual"}
                                                                onChange={() => setMode("manual")}
                                                            />
                                                            <span className="form-selectgroup-label d-flex align-items-center p-3">
                                                                <span className="me-3">
                                                                    <span className="form-selectgroup-check"></span>
                                                                </span>
                                                                <span className="form-selectgroup-label-content text-start">
                                                                    <span className="form-selectgroup-title">Manual Update</span>
                                                                    <span className="text-muted d-block mt-1">
                                                                        Manually enter a custom registration number format.
                                                                    </span>
                                                                </span>
                                                            </span>
                                                        </label>
                                                    </div>
                                                </div>

                                                {mode === "auto" ? (
                                                    <div className="form-group mb-4">
                                                        <label className="form-label">Admission/Enrolment Session</label>
                                                        <select
                                                            className="form-control form-select"
                                                            value={admissionSession}
                                                            onChange={(e) => setAdmissionSession(e.target.value)}
                                                        >
                                                            <option value="">Select Session</option>
                                                            {settingsList
                                                                .sort((a, b) => b.Session.localeCompare(a.Session))
                                                                .map((item, index) => (
                                                                    <option key={index} value={item.Session}>
                                                                        {item.Session} {item.Status.toString() === "1" ? " (Active Session)" : ""}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                        <small className="text-muted mt-1 d-block">
                                                            The new registration number will follow the structure: <code>KPT/ODFeL/{
                                                                (collegesList.find(c => c.COL_ID.toString() === selectedCollegeId.toString())?.COL_CODE) || selectedStudent.COL_CODE
                                                            }/[YY]/[SEQ]</code>.
                                                        </small>
                                                    </div>
                                                ) : (
                                                    <div className="form-group mb-4">
                                                        <label className="form-label">New Registration Number</label>
                                                        <input 
                                                            type="text" 
                                                            className="form-control" 
                                                            placeholder="e.g. KPT/ODFeL/CST/22/001"
                                                            value={newRegNo}
                                                            onChange={(e) => setNewRegNo(e.target.value)}
                                                        />
                                                        <small className="text-muted mt-1 d-block">
                                                            Ensure the format is correct. Standard format is: <code>School/Programme/CollegeCode/Year/Sequence</code>
                                                        </small>
                                                    </div>
                                                )}

                                                <div className="d-flex">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-primary ms-auto"
                                                        onClick={handleRegenerate}
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                                Updating...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                                    <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -5v5h5" />
                                                                    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 5v-5h-5" />
                                                                </svg>
                                                                Process Update
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

const mapStateToProps = (state) => {
    return {
        loginData: state.loginDetails,
    };
};

export default connect(mapStateToProps, null)(RegenerateStudentRegNo);
